// src/components/Pricing.jsx
import React, { Component } from "react";
import axios from "axios";

class Pricing extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      products: [],
      error: null,
      busyAction: null,
      hasActiveSubscription: false,
      creditsBalance: 0,
    };

    this.API_BASE = process.env.REACT_APP_API_ENDPOINT;
  }

  async componentDidMount() {
    await this.fetchProducts();
    await this.fetchSubscriptionStatus();
    await this.loadCredits();
  }

  /* ----------------------------
     FETCH PRODUCTS
  ----------------------------- */
  async fetchProducts() {
    try {
      const res = await axios.get(`${this.API_BASE}/stripe/products`, {
        withCredentials: true,
      });

      this.setState({ products: res.data.products || [], loading: false });
    } catch (err) {
      console.error("Error loading products", err);
      this.setState({
        error: "Unable to load pricing from server",
        loading: false,
      });
    }
  }

  /* ----------------------------
     SUBSCRIPTION STATUS
  ----------------------------- */
  async fetchSubscriptionStatus() {
    try {
      const res = await axios.get(
        `${this.API_BASE}/stripe/subscriptions/history`,
        { withCredentials: true }
      );

      const subs = res.data.subscriptions || [];

      const active = subs.some(
        (s) => s.status === "active" || s.status === "trialing"
      );

      this.setState({ hasActiveSubscription: active });
    } catch (err) {
      console.error("Error loading subscription status:", err);
    }
  }

  /* ----------------------------
     CREDIT BALANCE
  ----------------------------- */
  loadCredits = async () => {
    try {
      const res = await axios.get(
        `${this.API_BASE}/stripe/credits/balance`,
        { withCredentials: true }
      );

      this.setState({ creditsBalance: res.data.credits });
      return res.data.credits;
    } catch (err) {
      console.error("Cannot load credits", err);
      return 0;
    }
  };

  /* ----------------------------
     PRODUCT FILTERS
  ----------------------------- */
  getSubscriptionProducts() {
    return this.state.products.filter((p) =>
      p.prices.some((price) => price.recurring)
    );
  }

  getCreditProducts() {
    return this.state.products.filter((p) =>
      p.prices.some((price) => !price.recurring)
    );
  }

  getProductFeatures(product) {
    let raw = product?.metadata?.features;

    if (!raw && product?.prices?.[0]?.metadata?.features) {
      raw = product.prices[0].metadata.features;
    }

    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("Error parsing features for product", product.id, e);
      return [];
    }
  }

  /* ----------------------------
     CHECKOUT: SUBSCRIPTION
  ----------------------------- */
  handleSubscribe = async (lookupKey) => {
    try {
      this.setState({ busyAction: "subscribe" });

      const res = await axios.post(
        `${this.API_BASE}/stripe/subscribe/session`,
        { lookupKey },
        { withCredentials: true }
      );

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        alert("Unable to start subscription checkout");
      }
    } catch (err) {
      console.error("Subscribe error", err);
      alert("Unable to start subscription.");
    } finally {
      this.setState({ busyAction: null });
    }
  };

  /* ----------------------------
     CHECKOUT: TOP-UP
  ----------------------------- */
  handleTopup = async (lookupKey) => {
    try {
      this.setState({ busyAction: "topup" });

      const res = await axios.post(
        `${this.API_BASE}/stripe/topup/session`,
        { lookupKey },
        { withCredentials: true }
      );

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        alert("Unable to start credit checkout");
      }
    } catch (err) {
      console.error("Topup error", err);
      alert("Unable to top up credits.");
    } finally {
      this.setState({ busyAction: null });
    }
  };

  /* ----------------------------
     RENDER PRICE TAG
  ----------------------------- */
  renderPriceTag(price) {
    if (!price) return null;

    const amount = (price.unit_amount || 0) / 100;
    const currency = (price.currency || "aud").toUpperCase();

    if (price.recurring) {
      return (
        <div className="text-center">
          <span className="text-4xl font-bold text-slate-900">
            {currency} {amount.toFixed(2)}
          </span>
          <div className="mt-1 text-sm text-slate-500">
            per {price.recurring.interval}
          </div>
        </div>
      );
    }

    return (
      <div className="text-center">
        <span className="text-3xl font-bold text-slate-900">
          {currency} {amount.toFixed(2)}
        </span>
        <div className="mt-1 text-sm text-slate-500">one-off purchase</div>
      </div>
    );
  }

  /* ----------------------------
     RENDER PRODUCT CARD
  ----------------------------- */
  renderProductCard(product, isSubscription) {
    const price = product.prices[0];
    const lookupKey = price.lookup_key;
    const features = this.getProductFeatures(product);

    return (
      <div
        key={product.id}
        className="group flex h-full min-h-[520px] w-full max-w-[420px] flex-col justify-between overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg transition duration-200 hover:-translate-y-1 hover:shadow-2xl"
      >
        <div>
          <div className="bg-gradient-to-r from-sky-900 via-sky-800 to-cyan-700 px-6 py-5 text-white">
            <div className="text-center">
              <h3 className="text-2xl font-bold tracking-tight">
                {product.name}
              </h3>

              {product.description && (
                <p className="mt-2 text-sm text-white/80">
                  {product.description}
                </p>
              )}
            </div>
          </div>

          <div className="p-6">
            <div className="mb-8">{this.renderPriceTag(price)}</div>

            {features.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 text-sm font-semibold text-slate-800">
                  Included
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  {features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-sky-600" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 pt-0">
          {isSubscription ? (
            <button
              disabled={
                this.state.busyAction === "subscribe" ||
                this.state.hasActiveSubscription
              }
              className={
                "w-full rounded-2xl px-4 py-3 text-base font-semibold text-white shadow-md transition " +
                (this.state.hasActiveSubscription
                  ? "cursor-not-allowed bg-slate-300 text-slate-500 shadow-none"
                  : "bg-gradient-to-r from-sky-700 to-cyan-700 hover:from-sky-800 hover:to-cyan-800")
              }
              onClick={() =>
                !this.state.hasActiveSubscription &&
                this.handleSubscribe(lookupKey)
              }
            >
              {this.state.hasActiveSubscription
                ? "Already Subscribed"
                : this.state.busyAction === "subscribe"
                ? "Starting subscription…"
                : "Subscribe"}
            </button>
          ) : (
            <button
              disabled={this.state.busyAction === "topup"}
              className="w-full rounded-2xl bg-white px-4 py-3 text-base font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200 transition hover:bg-sky-50 disabled:bg-slate-100"
              onClick={() => this.handleTopup(lookupKey)}
            >
              {this.state.busyAction === "topup"
                ? "Opening checkout…"
                : "Buy credits"}
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ----------------------------
     MAIN RENDER
  ----------------------------- */
  render() {
    const { loading, error, creditsBalance } = this.state;

    if (loading) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-slate-600 shadow-sm">
          Loading pricing…
        </div>
      );
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
          {error}
        </div>
      );
    }

    const subscriptionProducts = this.getSubscriptionProducts();
    const creditProducts = this.getCreditProducts();

    return (
      <div className="space-y-12">
        {/* HERO */}
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div className="bg-gradient-to-r from-slate-900 via-sky-900 to-cyan-800 px-6 py-10 text-white">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-4xl font-bold tracking-tight">Pricing</h2>
              <p className="mt-3 text-base text-white/80">
                Choose a monthly subscription to access Caredevo, then top up AI
                credits whenever you need more usage.
              </p>
            </div>
          </div>
        </section>

        {/* SUBSCRIPTION */}
        <section className="space-y-6">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-slate-900">Subscription</h3>
            <p className="mt-1 text-sm text-slate-500">
              Fixed monthly access to Caredevo.
            </p>
          </div>

          <div className="grid grid-cols-1 place-items-center gap-6">
            {subscriptionProducts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500">
                No subscription plans configured.
              </div>
            ) : (
              subscriptionProducts.map((p) => this.renderProductCard(p, true))
            )}
          </div>
        </section>

        {/* CREDIT PACKS */}
        {this.state.hasActiveSubscription && (
          <section className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900">
                Credit Packs
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Buy extra AI credits whenever you need more usage.
              </p>

              <div className="mt-4 inline-flex rounded-full bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-800 ring-1 ring-sky-200">
                Current Credits: {creditsBalance}
              </div>
            </div>

            <div className="grid grid-cols-3 place-items-center gap-6 sm:grid-cols-1 xs:grid-cols-1">
              {creditProducts.length === 0 ? (
                <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500">
                  No credit packs configured.
                </div>
              ) : (
                creditProducts.map((p) => this.renderProductCard(p, false))
              )}
            </div>
          </section>
        )}
      </div>
    );
  }
}

export default Pricing;